import { useState } from 'react';
import { deleteOrgFile } from '../../storage/FileSystemAdapter';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { CRITICALITY_LEVELS, CUSTOMER_STATUSES, CUSTOMER_TYPES } from '../../utils/enums';
import Badge from '../shared/Badge';
import { BG_CONTENT, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_WARN, STATUS_INFO, SURFACE_WHITE, FONT_BODY, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_LIST_ITEM } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope', 'customer': 'customers',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const CRITICALITIES = CRITICALITY_LEVELS;

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    loading:               'Cargando clientes…',
    emptyTitle:            'No hay clientes registrados.',
    emptyHint:             'Crea el primer cliente con el botón "+ Nuevo cliente".',
    colRef:                'Referencia',
    colName:               'Nombre',
    colSector:             'Sector',
    colType:               'Tipo',
    colCriticality:        'Criticidad',
    colStatus:             'Estado',
    colSites:              'Sites',
    colUpdated:            'Actualizado',
    colActions:            'Acciones',
    filterAll:             'Todos',
    filterName:            'Nombre',
    filterNamePh:          'Buscar…',
    filterType:            'Tipo',
    filterCriticality:     'Criticidad',
    filterStatus:          'Estado',
    viewBtn:               'Ver',
    editBtn:               'Editar',
    deleteBtn:             'Eliminar',
    deleteConfirm:         '¿Eliminar el cliente "{name}"? Esta acción no se puede deshacer.',
    selectAll:             'Seleccionar todo',
    nSelected:             '{n} seleccionados',
    deleteSelected:        'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar los siguientes clientes?\n\n{names}\n\nEsta acción no se puede deshacer.',
    sitesCount:            '{n} sites · {m} países',
    saveError:             'Error al guardar. Inténtalo de nuevo.',
    formCancel:            'Cancelar',
    typeLabels: {
      'Corporate':     'Corporativo',
      'SMB':           'PYME',
      'Public sector': 'Sector público',
      'Academic':      'Académico',
      'Other':         'Otro',
    },
    criticalityLabels: {
      Critical: 'Crítico',
      High:     'Alta',
      Medium:   'Media',
      Low:      'Baja',
    },
    statusLabels: {
      'Prospective': 'Prospecto',
      'Active':      'Activo',
      'Paused':      'Pausado',
      'Terminated':  'Terminado',
    },
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    deleteBlockedHolder_customer_one:   'cliente',
    deleteBlockedHolder_customer_other: 'clientes',
  },
  en: {
    loading:               'Loading customers…',
    emptyTitle:            'No customers recorded yet.',
    emptyHint:             'Create the first customer using the "+ New customer" button.',
    colRef:                'Reference',
    colName:               'Name',
    colSector:             'Sector',
    colType:               'Type',
    colCriticality:        'Criticality',
    colStatus:             'Status',
    colSites:              'Sites',
    colUpdated:            'Updated',
    colActions:            'Actions',
    filterAll:             'All',
    filterName:            'Name',
    filterNamePh:          'Search…',
    filterType:            'Type',
    filterCriticality:     'Criticality',
    filterStatus:          'Status',
    viewBtn:               'View',
    editBtn:               'Edit',
    deleteBtn:             'Delete',
    deleteConfirm:         'Delete customer "{name}"? This cannot be undone.',
    selectAll:             'Select all',
    nSelected:             '{n} selected',
    deleteSelected:        'Delete selected',
    deleteSelectedConfirm: 'Delete the following customers?\n\n{names}\n\nThis cannot be undone.',
    sitesCount:            '{n} sites · {m} countries',
    saveError:             'Could not save. Please try again.',
    formCancel:            'Cancel',
    typeLabels: {
      'Corporate':     'Corporate',
      'SMB':           'SMB',
      'Public sector': 'Public sector',
      'Academic':      'Academic',
      'Other':         'Other',
    },
    criticalityLabels: {
      Critical: 'Critical',
      High:     'High',
      Medium:   'Medium',
      Low:      'Low',
    },
    statusLabels: {
      'Prospective': 'Prospective',
      'Active':      'Active',
      'Paused':      'Paused',
      'Terminated':  'Terminated',
    },
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    deleteBlockedHolder_customer_one:   'customer',
    deleteBlockedHolder_customer_other: 'customers',
  },
};

// ── COLOR MAPS ────────────────────────────────────────────────────────────────
const CRITICALITY_COLORS = {
  Critical: { bg: ERROR_BG, text: ERROR_TEXT },
  High:     { bg: ERROR_BG, text: ERROR_TEXT },
  Medium:   { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  Low:      { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const STATUS_COLORS = {
  'Prospective': { bg: STATUS_INFO.bg, text: STATUS_INFO.text },
  'Active':      { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  'Paused':      { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Terminated':  { bg: BG_MUTED, text: TEXT_MUTED },
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function CustomerList({ customers, loading, org, lang, onNew, onEdit, onView, onNavigate, onCustomersChanged }) {
  const t = T[lang] || T.en;

  const [filterText,        setFilterText]        = useState('');
  const [filterType,        setFilterType]        = useState('');
  const [filterCriticality, setFilterCriticality] = useState('');
  const [filterStatus,      setFilterStatus]      = useState('');
  const [selectedIds,       setSelectedIds]       = useState(new Set());
  const [listError,         setListError]         = useState('');
  const [confirmState,      setConfirmState]      = useState(null);
  const [scanningDelete,    setScanningDelete]    = useState(false);

  const showError = (msg) => { setListError(msg); setTimeout(() => setListError(''), 4000); };

  const safeCustomers = Array.isArray(customers) ? customers : [];

  const countSiteCountries = (c) => {
    const sites = Array.isArray(c.sites) ? c.sites : [];
    const countries = new Set();
    for (const s of sites) {
      if (s && s.country) countries.add(s.country);
    }
    return { sites: sites.length, countries: countries.size };
  };

  const filtered = safeCustomers.filter((c) => {
    if (filterText) {
      const q = filterText.trim().toLowerCase();
      if (q && !(c.name || '').toLowerCase().includes(q)) return false;
    }
    if (filterType        && c.customerType !== filterType)        return false;
    if (filterCriticality && c.criticality  !== filterCriticality) return false;
    if (filterStatus      && c.status       !== filterStatus)      return false;
    return true;
  });

  const selectedInView = filtered.filter((c) => selectedIds.has(c.id));
  const allChecked     = filtered.length > 0 && selectedInView.length === filtered.length;
  const someChecked    = selectedInView.length > 0;

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(filtered.map((c) => c.id)));
    else         setSelectedIds(new Set());
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const handleDeleteOne = async (customer) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'customer', customer.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const msg = t.deleteConfirm.replace('{name}', customer.name);
      setConfirmState({
        message: msg,
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `customers/${customer.id}.json`);
            setSelectedIds((prev) => { const next = new Set(prev); next.delete(customer.id); return next; });
            onCustomersChanged();
          } catch { showError(t.saveError); }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  const handleDeleteSelected = async () => {
    if (!selectedInView.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    const items = [...selectedInView];
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(c => scanReferencesToEntity(org.id, 'customer', c.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((c, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(c);
      else blocked.push({ record: c, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      await Promise.all(cohort.map((c) => deleteOrgFile(org.id, `customers/${c.id}.json`).catch(() => {})));
      setSelectedIds(new Set());
      onCustomersChanged();
    };
    if (blocked.length === 0) {
      const names = items.map((c) => c.name).join(', ');
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.name || record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const thSt = {
    ...FONT_TINY, color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    padding: '0.4rem 0.75rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
    fontWeight: 600, textAlign: 'left', background: BG_CONTENT, whiteSpace: 'nowrap',
  };
  const tdSt = {
    ...FONT_LIST_ITEM,
    padding: '0.55rem 0.75rem', borderBottom: `1px solid ${BG_SHELL}`,
    verticalAlign: 'middle',
  };
  const filterSelectSt = {
    ...FONT_SMALL,
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5,
    padding: '0.3rem 0.5rem', background: SURFACE_WHITE, color: TEXT_DEFAULT, cursor: 'pointer',
  };
  const btnSmall = {
    ...BTN_SECONDARY,
    ...FONT_TINY,
    borderRadius: 5,
    padding: '0.2rem 0.5rem', cursor: 'pointer',
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {listError && (
        <div style={{ ...FONT_LABEL, marginBottom: '0.75rem', padding: '0.6rem 1rem', background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8, color: ERROR_TEXT }}>
          {listError}
        </div>
      )}
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterName}</span>
            <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder={t.filterNamePh}
              style={{ ...filterSelectSt, minWidth: 180, cursor: 'text' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterType}</span>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {CUSTOMER_TYPES.map((v) => <option key={v} value={v}>{t.typeLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterCriticality}</span>
            <select value={filterCriticality} onChange={(e) => setFilterCriticality(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {CRITICALITIES.map((v) => <option key={v} value={v}>{t.criticalityLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {CUSTOMER_STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', selectedInView.length)}
          </span>
          <button onClick={handleDeleteSelected} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>
            {t.deleteSelected}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>{t.loading}</p>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '2.5rem', textAlign: 'center' }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>{t.emptyTitle}</p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.emptyHint}</p>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={t.selectAll}
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={thSt}>{t.colRef}</th>
                <th style={{ ...thSt, minWidth: 160 }}>{t.colName}</th>
                <th style={thSt}>{t.colSector}</th>
                <th style={thSt}>{t.colType}</th>
                <th style={thSt}>{t.colCriticality}</th>
                <th style={thSt}>{t.colStatus}</th>
                <th style={thSt}>{t.colSites}</th>
                <th style={thSt}>{t.colUpdated}</th>
                <th style={thSt}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => {
                const { sites, countries } = countSiteCountries(c);
                return (
                  <tr key={c.id} style={{ background: idx % 2 === 0 ? BG_SURFACE : 'transparent' }}>
                    <td style={{ ...tdSt, textAlign: 'center', padding: '0.55rem 0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={(e) => toggleOne(c.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {c.ref || c.id}
                    </td>
                    <td style={{ ...tdSt, fontWeight: 600, color: TEXT_HEADING }}>{c.name}</td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED }}>{c.sector || '—'}</td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED }}>{t.typeLabels[c.customerType] || c.customerType || '—'}</td>
                    <td style={tdSt}>
                      {c.criticality
                        ? <Badge label={c.criticality} displayLabel={t.criticalityLabels[c.criticality] || c.criticality} colorMap={CRITICALITY_COLORS} />
                        : <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>—</span>}
                    </td>
                    <td style={tdSt}>
                      {c.status
                        ? <Badge label={c.status} displayLabel={t.statusLabels[c.status] || c.status} colorMap={STATUS_COLORS} />
                        : <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>—</span>}
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {t.sitesCount.replace('{n}', sites).replace('{m}', countries)}
                    </td>
                    <td style={{ ...tdSt, ...FONT_TINY, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {c.updatedAt ? c.updatedAt.slice(0, 10) : '—'}
                    </td>
                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => onView(c)} style={btnSmall}>{t.viewBtn}</button>
                        <button onClick={() => onEdit(c)} style={btnSmall}>{t.editBtn}</button>
                        <button onClick={() => handleDeleteOne(c)} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>
                          {t.deleteBtn}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
